import { SUPPLIER_TYPE_LABELS as TYPE_LABELS, formatSupplierTypes } from '../../utils/supplierTypes';
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import type { Column } from 'write-excel-file/browser';
import { Supplier, SupplierExportRow, suppliersAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import {
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  BuildingOfficeIcon,
  TruckIcon,
  FunnelIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowPathIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  CubeIcon,
  AdjustmentsHorizontalIcon,
} from '@heroicons/react/24/outline';
import { Card, CardBody, Button, Badge, Spinner, EmptyState, PageHero } from '../ui';
import SupplierCategoriesSection from './SupplierCategoriesSection';
import SupplierProductsSection from './SupplierProductsSection';
import ExcelImportModal from '../common/ExcelImportModal';
import { ExcelImportRow, supplierImportSchema } from '../../utils/excelImport';

const EXPORT_BATCH_SIZE = 250;

const formatEnumLabel = (value: string) => value
  .toLowerCase()
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (character) => character.toUpperCase());

const getExportConfig = (status?: SupplierSectionProps['status']) => {
  if (status === 'ACTIVE_SUPPLIER') {
    return { filePrefix: 'suppliers', sheetName: 'Suppliers' };
  }
  if (status === 'POTENTIAL_SUPPLIER') {
    return { filePrefix: 'potential-suppliers', sheetName: 'Potential Suppliers' };
  }
  return { filePrefix: 'vendors', sheetName: 'Vendors' };
};

const getSupplierTypeIcon = (type: string) => {
  switch (type) {
    case 'MANUFACTURER':
    case 'CONTRACT_MANUFACTURER':
      return <BuildingOfficeIcon className="h-5 w-5" />;
    case 'DISTRIBUTOR':
    case 'WHOLESALER':
      return <TruckIcon className="h-5 w-5" />;
    default:
      return <BuildingOfficeIcon className="h-5 w-5" />;
  }
};

const getImportanceVariant = (importance: string): 'error' | 'warning' | 'primary' | 'neutral' => {
  const variants: Record<string, 'error' | 'warning' | 'primary' | 'neutral'> = {
    VERY_HIGH: 'error',
    HIGH: 'warning',
    MEDIUM: 'warning',
    LOW: 'primary',
    VERY_LOW: 'neutral',
  };
  return variants[importance] || 'neutral';
};

/* ───────────────────────── Section Component ───────────────────────── */

interface SupplierSectionProps {
  status?: 'ACTIVE_SUPPLIER' | 'POTENTIAL_SUPPLIER';
}

const SupplierSection: React.FC<SupplierSectionProps> = ({ status }) => {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 0 });
  const [showFilters, setShowFilters] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [productSearchInput, setProductSearchInput] = useState('');
  const [filters, setFilters] = useState({ search: '', productSearch: '', type: '', importance: '', nameSort: '' });

  const fetchSuppliers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await suppliersAPI.getAll({
        page: pagination.page,
        limit: pagination.limit,
        search: filters.search || undefined,
        productSearch: filters.productSearch || undefined,
        type: filters.type || undefined,
        importance: filters.importance || undefined,
        nameSort: (filters.nameSort || undefined) as 'asc' | 'desc' | undefined,
        status,
      });
      setSuppliers(data.data);
      setPagination(data.pagination);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch suppliers');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filters, status]);

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: searchInput }));
      setPagination(prev => ({ ...prev, page: 1 }));
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(prev => ({ ...prev, productSearch: productSearchInput }));
      setPagination(prev => ({ ...prev, page: 1 }));
    }, 500);
    return () => clearTimeout(timer);
  }, [productSearchInput]);

  const handleDelete = async (id: number) => {
    try {
      await suppliersAPI.delete(id);
      fetchSuppliers();
      setDeleteConfirm(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete supplier');
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setSearchInput('');
    setProductSearchInput('');
    setFilters({ search: '', productSearch: '', type: '', importance: '', nameSort: '' });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      setError('');

      const rows: SupplierExportRow[] = [];
      let exportPage = 1;
      let exportPages = 1;

      while (exportPage <= exportPages) {
        const response = await suppliersAPI.getExportPage({
          page: exportPage,
          limit: EXPORT_BATCH_SIZE,
          search: filters.search || undefined,
          productSearch: filters.productSearch || undefined,
          type: filters.type || undefined,
          importance: filters.importance || undefined,
          nameSort: (filters.nameSort || undefined) as 'asc' | 'desc' | undefined,
          status,
        });
        rows.push(...response.data);
        exportPages = response.pagination.pages;
        exportPage += 1;
      }

      if (rows.length === 0) {
        setError('No suppliers to export');
        return;
      }

      const header = (value: string) => ({
        value,
        fontWeight: 'bold' as const,
        backgroundColor: '#DBEAFE',
        align: 'center' as const,
      });
      const columns: Column<SupplierExportRow>[] = [
        { header: header('Supplier'), width: 30, cell: (supplier) => supplier.name },
        {
          header: header('Type'),
          width: 24,
          cell: (supplier) => formatSupplierTypes(supplier),
        },
        { header: header('Email'), width: 32, cell: (supplier) => supplier.emails[0] || '' },
        { header: header('Phone'), width: 20, cell: (supplier) => supplier.phoneNumbers[0] || '' },
        { header: header('Importance'), width: 16, cell: (supplier) => formatEnumLabel(supplier.importance) },
        { header: header('Deals'), width: 12, cell: (supplier) => supplier._count.deals },
      ];
      const { default: writeExcelFile } = await import('write-excel-file/browser');
      const exportConfig = getExportConfig(status);
      const date = new Date().toISOString().split('T')[0];

      await writeExcelFile(rows, {
        columns,
        sheet: exportConfig.sheetName,
        stickyRowsCount: 1,
      }).toFile(`${exportConfig.filePrefix}_${date}.xlsx`);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to export suppliers');
    } finally {
      setExporting(false);
    }
  };

  const hasActiveFilters = filters.search || filters.productSearch || filters.type || filters.importance || filters.nameSort;
  const activeFilterCount = [filters.search, filters.productSearch, filters.type, filters.importance, filters.nameSort].filter(Boolean).length;

  const renderPaginationButtons = () => {
    const buttons = [];
    const maxButtons = 5;
    const { page, pages } = pagination;

    if (pages <= maxButtons) {
      for (let i = 1; i <= pages; i++) {
        buttons.push(
          <button
            key={i}
            onClick={() => setPagination(prev => ({ ...prev, page: i }))}
            className={`px-3 py-1 rounded ${page === i ? 'bg-primary-600 text-white' : 'bg-white text-neutral-700 hover:bg-neutral-50'}`}
          >
            {i}
          </button>
        );
      }
    } else {
      buttons.push(
        <button key={1} onClick={() => setPagination(prev => ({ ...prev, page: 1 }))}
          className={`px-3 py-1 rounded ${page === 1 ? 'bg-primary-600 text-white' : 'bg-white text-neutral-700 hover:bg-neutral-50'}`}>1</button>
      );
      if (page > 3) buttons.push(<span key="e1" className="px-2">...</span>);
      for (let i = Math.max(2, page - 1); i <= Math.min(pages - 1, page + 1); i++) {
        buttons.push(
          <button key={i} onClick={() => setPagination(prev => ({ ...prev, page: i }))}
            className={`px-3 py-1 rounded ${page === i ? 'bg-primary-600 text-white' : 'bg-white text-neutral-700 hover:bg-neutral-50'}`}>{i}</button>
        );
      }
      if (page < pages - 2) buttons.push(<span key="e2" className="px-2">...</span>);
      buttons.push(
        <button key={pages} onClick={() => setPagination(prev => ({ ...prev, page: pages }))}
          className={`px-3 py-1 rounded ${page === pages ? 'bg-primary-600 text-white' : 'bg-white text-neutral-700 hover:bg-neutral-50'}`}>{pages}</button>
      );
    }
    return buttons;
  };

  return (
    <div>
      {error && (
        <Card variant="bordered" className="mb-4 border-error-200 bg-error-50">
          <CardBody className="p-4">
            <div className="text-sm text-error-700">{error}</div>
          </CardBody>
        </Card>
      )}

      {/* Filters */}
      <Card variant="elevated" className="mb-4 overflow-hidden">
        <CardBody className="p-0">
          <div className="border-b border-neutral-200 bg-neutral-50/70 px-4 py-3">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-center gap-2">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                  <FunnelIcon className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-neutral-900">Filter suppliers</h3>
                  <p className="text-xs text-neutral-500">
                    {pagination.total} result{pagination.total === 1 ? '' : 's'}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {hasActiveFilters && (
                  <Badge variant="primary" size="sm">{activeFilterCount} active</Badge>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExport}
                  isLoading={exporting}
                  disabled={loading || exporting || pagination.total === 0}
                  leftIcon={<ArrowDownTrayIcon className="h-4 w-4" />}
                >
                  Export Excel
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                  leftIcon={<AdjustmentsHorizontalIcon className="h-4 w-4" />}
                >
                  {showFilters ? 'Hide filters' : 'More filters'}
                </Button>
                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    leftIcon={<XMarkIcon className="h-4 w-4" />}
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="p-4">
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              <div>
                <label className="mb-1.5 flex items-center gap-1.5 text-sm font-medium text-neutral-700">
                  <MagnifyingGlassIcon className="h-4 w-4 text-neutral-400" />
                  Supplier search
                </label>
                <div className="relative">
                  <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-neutral-400" />
                  <input
                    type="text"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Name, email, or phone"
                    className="w-full rounded-lg border border-neutral-300 py-2.5 pl-10 pr-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
              <div>
                <label className="mb-1.5 flex items-center gap-1.5 text-sm font-medium text-neutral-700">
                  <CubeIcon className="h-4 w-4 text-neutral-400" />
                  Product catalogue search
                </label>
                <div className="relative">
                  <CubeIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-neutral-400" />
                  <input
                    type="text"
                    value={productSearchInput}
                    onChange={(e) => setProductSearchInput(e.target.value)}
                    placeholder="Product name in supplier catalogue"
                    className="w-full rounded-lg border border-neutral-300 py-2.5 pl-10 pr-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
            </div>

            {showFilters && (
              <div className="mt-4 grid grid-cols-1 gap-4 border-t border-neutral-200 pt-4 md:grid-cols-3">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Type</label>
                  <select value={filters.type} onChange={(e) => handleFilterChange('type', e.target.value)}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors">
                    <option value="">All Types</option>
                    {Object.entries(TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Importance</label>
                  <select value={filters.importance} onChange={(e) => handleFilterChange('importance', e.target.value)}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors">
                    <option value="">All Levels</option>
                    <option value="VERY_HIGH">Very High</option>
                    <option value="HIGH">High</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="LOW">Low</option>
                    <option value="VERY_LOW">Very Low</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="supplier-name-order" className="block text-sm font-medium text-neutral-700 mb-1">Name order</label>
                  <select id="supplier-name-order" value={filters.nameSort} onChange={(e) => handleFilterChange('nameSort', e.target.value)}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors">
                    <option value="">Default (Newest)</option>
                    <option value="asc">Name: A–Z</option>
                    <option value="desc">Name: Z–A</option>
                  </select>
                </div>
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      {/* Table */}
      <Card variant="elevated" className="overflow-hidden">
        {loading && suppliers.length === 0 ? (
          <div className="flex justify-center items-center h-40"><Spinner size="lg" /></div>
        ) : (
          <>
            <table className="min-w-full divide-y divide-neutral-200">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Supplier</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Importance</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Deals</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-neutral-200">
                {suppliers.map((supplier) => (
                  <tr key={supplier.id} className="hover:bg-neutral-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center bg-primary-100 rounded-full text-primary-600">
                          {getSupplierTypeIcon(supplier.type)}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-neutral-900">{supplier.name}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                      {formatSupplierTypes(supplier)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-neutral-900">{supplier.emails?.[0] || '-'}</div>
                      <div className="text-sm text-neutral-500">{supplier.phoneNumbers?.[0] || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge variant={getImportanceVariant(supplier.importance)} size="sm">
                        {supplier.importance.replace(/_/g, ' ')}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500">{supplier._count?.deals || 0}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <Link to={`/admin/suppliers/${supplier.id}`} className="text-primary-600 hover:text-primary-900 transition-colors">
                          <EyeIcon className="h-5 w-5" />
                        </Link>
                        <Link to={`/admin/suppliers/${supplier.id}/edit`} className="text-primary-600 hover:text-primary-900 transition-colors">
                          <PencilIcon className="h-5 w-5" />
                        </Link>
                        {deleteConfirm === supplier.id ? (
                          <div className="flex space-x-2">
                            <button onClick={() => handleDelete(supplier.id)} className="text-error-600 hover:text-error-900 font-medium transition-colors">Confirm</button>
                            <button onClick={() => setDeleteConfirm(null)} className="text-neutral-600 hover:text-neutral-900 transition-colors">Cancel</button>
                          </div>
                        ) : (
                          <button onClick={() => setDeleteConfirm(supplier.id)} className="text-error-600 hover:text-error-900 transition-colors">
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {suppliers.length === 0 && !loading && (
              <EmptyState
                icon={<BuildingOfficeIcon className="h-12 w-12" />}
                title="No suppliers found"
                description="No suppliers match your current filters."
              />
            )}
          </>
        )}
      </Card>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <Card variant="elevated" className="mt-4">
          <CardBody className="px-4 py-3">
            <div className="flex-1 flex justify-between sm:hidden">
              <Button variant="outline" size="sm"
                onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                disabled={pagination.page === 1}>Previous</Button>
              <Button variant="outline" size="sm"
                onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.pages, prev.page + 1) }))}
                disabled={pagination.page === pagination.pages}>Next</Button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <p className="text-sm text-neutral-700">
                Showing <span className="font-medium">{(pagination.page - 1) * pagination.limit + 1}</span> to{' '}
                <span className="font-medium">{Math.min(pagination.page * pagination.limit, pagination.total)}</span>{' '}
                of <span className="font-medium">{pagination.total}</span>
              </p>
              <div className="flex items-center space-x-2">
                <Button variant="outline" size="sm"
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                  disabled={pagination.page === 1}><ChevronLeftIcon className="h-5 w-5" /></Button>
                {renderPaginationButtons()}
                <Button variant="outline" size="sm"
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.pages, prev.page + 1) }))}
                  disabled={pagination.page === pagination.pages}><ChevronRightIcon className="h-5 w-5" /></Button>
              </div>
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
};

/* ──────────────── Deleted Suppliers Section ──────────────── */

const DeletedSupplierSection: React.FC = () => {
  const [deleted, setDeleted] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [restoringId, setRestoringId] = useState<number | null>(null);

  const fetchDeleted = async () => {
    try {
      setLoading(true);
      const data = await suppliersAPI.getDeleted();
      setDeleted(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch deleted suppliers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDeleted(); }, []);

  const handleRestore = async (id: number) => {
    setRestoringId(id);
    try {
      await suppliersAPI.restore(id);
      fetchDeleted();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to restore supplier');
    } finally {
      setRestoringId(null);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Spinner size="lg" /></div>;

  if (error) {
    return (
      <Card variant="bordered" className="border-error-200 bg-error-50">
        <CardBody><p className="text-sm text-error-700">{error}</p></CardBody>
      </Card>
    );
  }

  if (deleted.length === 0) {
    return (
      <EmptyState
        icon={<TrashIcon className="h-12 w-12" />}
        title="No deleted suppliers"
        description="Suppliers you delete will appear here and can be restored."
      />
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-neutral-500">{deleted.length} deleted supplier{deleted.length !== 1 ? 's' : ''}</p>
      {deleted.map((supplier) => (
        <Card key={supplier.id} variant="bordered" className="border-neutral-200 opacity-80">
          <CardBody className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-neutral-800">{supplier.name}</p>
                <p className="text-xs text-neutral-500 mt-0.5">
                  {formatSupplierTypes(supplier)}
                  {supplier.deletedAt && (
                    <> · Deleted {new Date(supplier.deletedAt).toLocaleDateString()}</>
                  )}
                </p>
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => handleRestore(supplier.id)}
                isLoading={restoringId === supplier.id}
                leftIcon={<ArrowPathIcon className="h-4 w-4" />}
              >
                Restore
              </Button>
            </div>
          </CardBody>
        </Card>
      ))}
    </div>
  );
};

/* ────────────────────────── Main Page with Tabs ────────────────────────── */


const TABS = [
  { key: 'ALL' as const, label: 'Vendors', icon: <BuildingOfficeIcon className="h-5 w-5" /> },
  { key: 'ACTIVE_SUPPLIER' as const, label: 'Suppliers', icon: <BuildingOfficeIcon className="h-5 w-5" /> },
  { key: 'POTENTIAL_SUPPLIER' as const, label: 'Potential Suppliers', icon: <TruckIcon className="h-5 w-5" /> },
  { key: 'DELETED' as const, label: 'Deleted', icon: <TrashIcon className="h-5 w-5" /> },
  { key: 'CATEGORIES' as const, label: 'Categories', icon: <BuildingOfficeIcon className="h-5 w-5" /> },
  { key: 'PRODUCTS' as const, label: 'Products', icon: <TruckIcon className="h-5 w-5" /> },
];

const SuppliersList: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'ALL' | 'ACTIVE_SUPPLIER' | 'POTENTIAL_SUPPLIER' | 'DELETED' | 'CATEGORIES' | 'PRODUCTS'>('ALL');
  const [showImportModal, setShowImportModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleImportSuppliers = async (rows: ExcelImportRow[]) => {
    await suppliersAPI.bulkImport(rows);
    setRefreshKey((current) => current + 1);
    setActiveTab('ALL');
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      <PageHero
        title="Suppliers"
        subtitle="Manage suppliers and track company expenses"
        icon={<TruckIcon className="w-5 h-5 lg:w-6 lg:h-6 text-white drop-shadow-lg" />}
        accentColor="orange"
      >
        <div className="mt-2 flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => setShowImportModal(true)}
            leftIcon={<ArrowUpTrayIcon className="h-5 w-5" />}
          >
            Import Excel
          </Button>
          <Link to="/admin/suppliers/new">
            <Button variant="primary" leftIcon={<PlusIcon className="h-5 w-5" />}>
              Add Supplier
            </Button>
          </Link>
        </div>
      </PageHero>

      {/* Tabs */}
      <div className="mt-6 border-b border-neutral-200">
        <nav className="-mb-px flex space-x-8 overflow-x-auto whitespace-nowrap" aria-label="Supplier tabs">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === tab.key
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'
                }`}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {activeTab === 'ALL' && <SupplierSection key={`all-${refreshKey}`} />}
        {activeTab === 'ACTIVE_SUPPLIER' && <SupplierSection key={`active-${refreshKey}`} status="ACTIVE_SUPPLIER" />}
        {activeTab === 'POTENTIAL_SUPPLIER' && <SupplierSection key={`potential-${refreshKey}`} status="POTENTIAL_SUPPLIER" />}
        {activeTab === 'DELETED' && <DeletedSupplierSection />}
        {activeTab === 'CATEGORIES' && <SupplierCategoriesSection />}
        {activeTab === 'PRODUCTS' && <SupplierProductsSection />}
      </div>

      <ExcelImportModal
        isOpen={showImportModal}
        title="Import Suppliers"
        schema={supplierImportSchema}
        rowsLabel="suppliers"
        templateFileName="suppliers-import-template.csv"
        onClose={() => setShowImportModal(false)}
        onConfirm={handleImportSuppliers}
      />
    </div>
  );
};


export default SuppliersList;
